import React, { Fragment } from 'react';
import 'animate.css';
import Icon from '../../Components/Icons/Icon';
import { Link } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { classNames } from '../../Services/helpers';
import { useSession } from '../../Components/Session';
import { useStatusBarColour } from '../../Components/Mobile';
import newMenu from '../newMenu';
import { useDialogues } from '../../Components/Dialogues';

const Menu = ({ isOpen, setIsOpen }) => {
    const {setShowRedesign, showDarkMode, setShowDarkMode, logout, hasFeature} = useSession();
    const {confirm, featureUnavailable} = useDialogues();
    useStatusBarColour(isOpen ? (showDarkMode ? '#1C273A' : '#EFF6FF') : null);

    return (
        <Transition.Root show={isOpen} as={Fragment} appear={true}>
            <Dialog
                as="div"
                className="relative z-30"
                onClose={() => setIsOpen(false)}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-50"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-50"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-blue-50/10 dark:bg-slate-900/10 backdrop-blur-lg" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-hidden">
                    <div className="absolute inset-0 overflow-hidden">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="-translate-x-full"
                            enterTo="translate-x-0"
                            leave="ease-in duration-200"
                            leaveFrom="translate-x-0"
                            leaveTo="-translate-x-full"
                        >
                            <Dialog.Panel className="fixed left-0 top-[5%] bottom-[5%] w-[90%] bg-slate-50 dark:bg-slate-800 rounded-r-3xl shadow-xl border dark:border-none border-l-0">
                                <div className="relative flex flex-col h-full">
                                    <button
                                        type="button"
                                        className="absolute right-6 top-4 p-3 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
                                        onClick={() => setIsOpen(false)}
                                        aria-label="Close menu"
                                    >
                                        <Icon {...{icon: 'XMark', size: '2x', className: 'fill-gray-800 dark:fill-gray-200'}} />
                                    </button>
                                    <div className="flex-1 px-6 py-4 overflow-y-auto no-scrollbar">
                                        {newMenu.map((section) => (
                                            <div key={section.title} className="mb-6">
                                                <div className="flex items-center mb-3">
                                                    <Icon
                                                        icon={section.icon}
                                                        size="1x"
                                                        className="fill-gray-800 dark:fill-gray-200 mr-3"
                                                    />
                                                    <h2 className="text-brand dark:text-brand-light font-bold text-base">{section.title}</h2>
                                                </div>

                                                <div className="ml-8 space-y-2">
                                                    {section.items.map((item) => {
                                                        const isLocked = item.feature && !hasFeature(item.feature);

                                                        if (isLocked) {
                                                            return (
                                                                <div
                                                                    key={item.label}
                                                                    onClick={() => {
                                                                        featureUnavailable(item.feature);
                                                                        setIsOpen(false);
                                                                    }}
                                                                    className={classNames(
                                                                        "py-1 transition-colors text-left cursor-pointer flex items-center justify-between",
                                                                        "text-gray-400 dark:text-gray-500"
                                                                    )}
                                                                >
                                                                    <span>{item.label}</span>
                                                                    <Icon icon="Lock" size="0.75x" className="fill-gray-400 dark:fill-gray-500" />
                                                                </div>
                                                            );
                                                        }

                                                        return (
                                                            <Link
                                                                key={item.label}
                                                                to={item.disabled ? null : item.to}
                                                                onClick={item.disabled ? null : setIsOpen}
                                                                className={classNames(
                                                                    "block py-1 transition-colors text-left",
                                                                    item.disabled ? "text-gray-400 dark:text-gray-500 cursor-not-allowed" : "text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                                                                )}
                                                            >
                                                                {item.label}
                                                            </Link>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}

                                        <div className="border-t border-gray-200 dark:border-gray-700 my-4"></div>

                                        <div className="space-y-2">
                                            <div className="flex items-center py-1">
                                                <Link to="/more" onClick={() => setIsOpen(false)}>
                                                    <Icon icon="Gear" size="1x" className="fill-gray-800 dark:fill-gray-200 mr-3" />
                                                    <span className="text-gray-800 dark:text-gray-200">Settings</span>
                                                </Link>
                                            </div>
                                            <div className="flex items-center py-1"
                                                onClick={() => confirm(
                                                    {theme: 'danger', title: 'Logout', message: 'Are you sure you want to logout?', confirmLabel: 'Logout'},
                                                    async () => {
                                                        await logout();
                                                        window.location.reload();
                                                    }
                                                )}
                                            >
                                                <Icon icon="ArrowLeftToBracket" size="1x" className="fill-gray-800 dark:fill-gray-200 mr-3" />
                                                <span className="text-gray-800 dark:text-gray-200">Log Out</span>
                                            </div>
                                        </div>

                                        <div className="border-t border-gray-200 dark:border-gray-700 my-4"></div>

                                        <div className="flex justify-start gap-4">
                                            <button
                                                className="w-8 h-8 rounded-full flex items-center justify-center border border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                                onClick={() => confirm(
                                                    {
                                                        theme: 'warning',
                                                        title: 'Turn off redesign?',
                                                        message: 'Are you sure you want to turn off the redesign?',
                                                        confirmLabel: 'Turn off',
                                                        cancelLabel: 'Cancel',
                                                    },
                                                    () => setShowRedesign(false)
                                                )}
                                            >
                                                <Icon icon="Brush" size="1x" className="fill-gray-800 dark:fill-gray-200" />
                                            </button>
                                            <button
                                                className="w-8 h-8 rounded-full flex items-center justify-center border border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                                onClick={() => setShowDarkMode(!showDarkMode)}
                                            >
                                                <Icon icon={showDarkMode ? 'SunBright' : 'Moon'} size="1x" className="fill-gray-800 dark:fill-gray-200" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}

export default Menu;
